import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0204 from "../lesson-04/lesson";
import lesson0327 from "../../course-03/lesson-04/lesson";

const linkLesson0204 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0204.id);
const linkLesson0304 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0327.id);

const theory = ` <p>The psychoacoustic parameter tonality describes the sensation elicited by distinct tones (sinusoids) or narrow-band noise signals and quantifies their effect on our perception of sound  [1]. Tonal sounds are often produced by periodicity, for instance, by rotating devices such as electric engines. Given the rising popularity of electric vehicles, tonality is becoming increasingly relevant. Tonal sounds can also be produced by impure tones or narrow-band noise, for example, by air flowing through a narrow tube. Products may emit tonally perceived noises not only due to pure tones or narrow-band noise, but also due to same-vicinity combinations of pure tones and narrow, elevated noise bands.<br class=newline>
<p>Tonality of a sound plays an important role in how the sound is perceived. For example, tonal noise is perceived as more annoying than comparable noise without tonal components. Additionally, undesired tonal components add to the annoyance of a sound [1]. For instance, an undesired tonal component in an electric engine might reduce customer acceptance. In contrast, tonal components can be a desired part of a sound (e.g. in music), so the context of the sound remains important.<br class=newline>
<p>The following metrics are currently in use as tonality metrics:
<ol class=enumerate1>
   <li class=enumerate id=x1-3x1>Tone-to-Noise Ratio (TTNR) [2]
   <li class=enumerate id=x1-5x2>Prominence Ratio (PR) [2]
   <li class=enumerate id=x1-7x3>DIN 45681 Tonality [3]
   <li class=enumerate id=x1-9x4>Tonality vs. Time (Aures/Terhardt) [4]
</ol>
<p>The TTNR, PR, and DIN 45681 Tonality methods have the following drawbacks [5]:
<ul class=itemize1>
   <li class=itemize>Do not consider critical bandwidth
   <li class=itemize>Do not consider cavum conchae resonance of the ear (an amplification of around 12 dB at about 4 kHz)
   <li class=itemize>Work only for discrete tones and not for narrow band noise or impure tones.
   <li class=itemize>Require high frequency resolution which leads to a low time resolution (refer to <a href="${linkLesson0304}" target="_blank">Lesson 3.4</a> for more information on the Fourier ). Therefore, frequency shifting tonal components with a short duration are either completely missed or under represented.
</ul>
<p>The Aures/Terhardt method has the following drawbacks [5]:
<ul class=itemize1>
   <li class=itemize>does not yield any spectral information
   <li class=itemize>Only shows the relationship of tonal to non-tonal loudness over time.
   <li class=itemize>not implemented completely
   <li class=itemize>As a result of its restriction to the time domain (with a high temporal resolution) and its sensitivity mainly to pure tones, the results from this method are difficult to interpret and rarely useful.
</ul>
<p>
   TTNR (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Δ</mi>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msub>
   </math>
   ) does not consider the absolute level of the tones but rather their relative level in comparison to the background sound. This is defined in ECMA-74 [2] as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <msub>
               <mrow>
                  <mi class=qopname>log</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>W</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>W</mi>
                           </mrow>
                           <mrow>
                              <mi>n</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-open>(</mo>
            <mstyle class=text>
               <mtext>dB</mtext>
            </mstyle>
            <mo class=MathClass-close>)</mo>
            
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>W</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>W</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   represent the power (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>Pa</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   ) of the tone and of the other components in the critical band respectively.<br class=newline>
<p>
   The reason behind this is that the level of a tone is irrelevant if the background sound isn’t considered. Sounds that contain distinct individual tones or narrow-band noise are perceived as tonal if the tonal component does not get masked by background noise. This is shown in Equation 1.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/09/theory/figure-1.svg alt="Figure 1. While the tone shown in (a) is visible in the frequency spectrum, it has a low TTNR and will be imperceptible due to masking. The tone depicted in (b), with a high enough TTNR (> 8 dB), will be clearly perceived as a distinct tone."><figcaption class=caption>Figure 1. While the tone shown in (a) is visible in the frequency spectrum, it has a low TTNR and will be imperceptible due to masking. The tone depicted in (b), with a high enough TTNR(> 8 dB), will be clearly perceived as a distinct tone.</figcaption></figure>
<p>According to the TTNR method, the tone level must be at least 8 dB above the level of the masking sound. For the PR method, a discrete tone needs to be at least 9 dB to be considered prominent. In ECMA-74 [2], PR is calculated as
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>Δ</mi>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>P</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <msub>
               <mrow>
                  <mi class=qopname>log</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
            <mrow>
               <mo fence=true form=prefix>[</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>W</mi>
                           </mrow>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi>W</mi>
                           </mrow>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>+</mo>
                        <msub>
                           <mrow>
                              <mi>W</mi>
                           </mrow>
                           <mrow>
                              <mi>U</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                        <mo class=MathClass-bin>∕</mo>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>]</mo>
            </mrow>
            <mo class=MathClass-open>(</mo>
            <mstyle class=text>
               <mtext>dB</mtext>
            </mstyle>
            <mo class=MathClass-close>)</mo>
            
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>W</mi>
         </mrow>
         <mrow>
            <mi>M</mi>
         </mrow>
      </msub>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>W</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>W</mi>
         </mrow>
         <mrow>
            <mi>U</mi>
         </mrow>
      </msub>
   </math>
   denote the power of the middle, lower, and upper critical bands respectively.<br class=newline>
<p>With many products becoming increasingly quieter, a flaw with these four methods of calculation is that they all tend to identify and penalize tonality magnitudes that are often near or below the threshold of hearing. A new Tonality method based on the Hearing Model of Sottek (HMS) addresses these issues by using psychoacoustic loudness in combination with a high frequency and time resolution. This new method, described in ECMA - 418-2 [4], considers the hearing threshold, masking depending on loudness level, and frequency-dependent masking (refer to <a href="${linkLesson0204}" target="_blank">Lesson 2.4</a> for more on masking). According to this method tonality is measured in tonality units HMS (tuHMS), with one tuHMS defined as the tonality of a 1 kHz 40 dB pure tone [1].<br class=newline>
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt style=text-align:left;white-space:nowrap id=TBL-3-5-1 class=td11>HMS
  <dd style=text-align:left id=TBL-3-5-2 class=td11>Hearing Model of Sottek
  <dt style=text-align:left;white-space:nowrap id=TBL-3-6-1 class=td11>PR
  <dd style=text-align:left id=TBL-3-6-2 class=td11>Prominence Ratio
  <dt style=text-align:left;white-space:nowrap id=TBL-3-7-1 class=td11>TTNR
  <dd style=text-align:left id=TBL-3-7-2 class=td11>Tone-to-Noise Ratio
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-becker2019 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Julian Becker, Roland Sottek, and Thiago Lobato. “Progress in Tonality Calculation”. en. In: <span class=cmti-10>Proceedings of the 23rd International Congress on </span><span class=cmti-10>Acoustics</span>. 2019, p. 8.
   <dt id=X0-ecma2021 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p><span class=cmti-10>ECMA-74 Measurement of Airborne Noise emitted by Information </span><span class=cmti-10>Technology and Telecommunications Equipment</span>. Tech. rep. 18. Ecma International, 2021.
   <dt id=X0-din2005 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p><span class=cmti-10>DIN 45681:2005-03 Akustik - Bestimmung der </span><span class=cmti-10>Tonhaltigkeit von Geräuschen und Ermittlung eines Tonzuschlages für die </span><span class=cmti-10>Beurteilung von Geräuschimmissionen</span>. Tech. rep. Beuth Verlag GmbH. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.31030/9601912 target="_blank">10.31030/9601912</a>. <span class=cmcsc-10><span class=small-caps>url</span></span>: <a class=url href=https://www.beuth.de/de/-/-/76463232 target="_blank"><span class=cmtt-10>https://www.beuth.de/de/-/-/76463232</span></a> (visited on 10/13/2021).
   <dt id=X0-ecma2020 class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p><span class=cmti-10>ECMA-418-2 Psychoacoustic metrics for ITT equipment — Part 2 (models </span><span class=cmti-10>based on human perception)</span>. Tech. rep. 2020.
   <dt id=X0-Bray class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>
      <p>Wade R. Bray. “A New Psychoacoustic Method for Reliable Measurement ofTonalities According to Perception”. English. In: <span class=cmti-10>INTER-NOISE 2018</span> (2018).
</dl>`;

export default theory;
